import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  FileText,
  Gauge,
  HelpCircle,
  Layers3,
  LayoutTemplate,
  ListChecks,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Send,
  Upload,
  Wrench,
  X,
} from "lucide-react";
import { useEquipment } from "../../../hooks/useEquipment";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { equipmentAPI } from "../../../services/equipmentAPI";
import { isSystemAdmin } from "../../../constants/systemRoles";
import {
  isProjectManager,
  isQaQcManager,
  isInspector,
  isProjectEngineer,
} from "../../../constants/projectRoles";
import ChecklistTemplatesView from "./equipment/ChecklistTemplatesView";
import DesignedChecklistPanel from "./equipment/DesignedChecklistPanel";

const STATUSES = [
  { id: "incomplete", label: "Document Review and Arrival", tone: "yellow" },
  { id: "assembled", label: "Assembled", tone: "blue" },
  { id: "installed", label: "Installed", tone: "orange" },
  { id: "pre_energization", label: "Pre-Energization", tone: "green" },
  {
    id: "energized_commissioned",
    label: "Energized & Commissioned",
    tone: "purple",
  },
];

// Higher lifecycle stages appear first in the Equipment Register.
const STATUS_ORDER = Object.fromEntries(
  STATUSES.map((status, index) => [status.id, index]),
);

const STATUS_META = {
  incomplete: {
    label: "Document Review and Arrival",
    dot: "bg-yellow-400",
    badge: "bg-yellow-100 text-yellow-900 ring-yellow-200",
    accent: "border-l-yellow-400",
    row: "bg-yellow-100/65 hover:bg-yellow-100/90",
  },
  assembled: {
    label: "Assembled / Ready for Delivery",
    dot: "bg-blue-500",
    badge: "bg-blue-100 text-blue-900 ring-blue-200",
    accent: "border-l-blue-500",
    row: "bg-blue-100/65 hover:bg-blue-100/90",
  },
  installed: {
    label: "Installed",
    dot: "bg-orange-500",
    badge: "bg-orange-100 text-orange-900 ring-orange-200",
    accent: "border-l-orange-500",
    row: "bg-orange-100/65 hover:bg-orange-100/90",
  },
  pre_energization: {
    label: "Pre-Energization Complete",
    dot: "bg-green-500",
    badge: "bg-green-100 text-green-900 ring-green-200",
    accent: "border-l-green-500",
    row: "bg-green-100/65 hover:bg-green-100/90",
  },
  energized_commissioned: {
    label: "Energized & Commissioned",
    dot: "bg-purple-500",
    badge: "bg-purple-100 text-purple-900 ring-purple-200",
    accent: "border-l-purple-500",
    row: "bg-purple-100/65 hover:bg-purple-100/90",
  },
};

const TASK_TYPES = [
  "Documentation",
  "Installation",
  "Missing Component",
  "Testing",
  "Verification",
  "Corrective Work",
  "Question / Clarification",
  "Other",
];

const EMPTY_CREATE = {
  equipment_tag: "",
  classification_name: "",
  type_name: "",
  room_name: "",
  skid_name: "",
  manufacturer: "",
  model_number: "",
  serial_number: "",
  description: "",
};

const EMPTY_TASK = {
  title: "",
  question: "",
  instructions: "",
  task_type: "Question / Clarification",
  lifecycle_stage: "incomplete",
  priority: "normal",
  assigned_to_user_id: "",
  due_date: "",
  checklist_item_id: null,
  checklist_instance_row_id: null,
  source_document_id: null,
};

const apiMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return error?.message || fallback;
};

const roleDisplayLabel = (role) =>
  String(role || "Other")
    .replaceAll("_", " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const equipmentActivityLabel = (entry) => {
  const meta = entry?.metadata || {};
  switch (entry?.action) {
    case "EQUIPMENT_CHECKLIST_FIELDS_SAVED":
      return `Updated ${meta.changed_field_count || 1} checklist field${Number(meta.changed_field_count || 1) === 1 ? "" : "s"}`;
    case "EQUIPMENT_CHECKLIST_ATTACHMENT_UPLOAD":
      return `Uploaded ${meta.document_name || "checklist document"}`;
    case "EQUIPMENT_CHECKLIST_ATTACHMENT_LINKED":
      return `Linked ${meta.document_name || "checklist document"}`;
    case "EQUIPMENT_CHECKLIST_ATTACHMENT_REMOVED":
      return `Removed ${meta.document_name || "checklist document"}`;
    case "EQUIPMENT_CHECKLIST_USER_ASSIGNED":
      return "Assigned checklist task";
    case "EQUIPMENT_CHECKLIST_TEMPLATE_SYNCED":
      return "Checklist updated from published template";
    case "EQUIPMENT_CHECKLIST_ASSIGNED_TO_EQUIPMENT":
      return "Checklist assigned to equipment";
    case "EQUIPMENT_CHECKLIST_UNLINKED_FROM_EQUIPMENT":
    case "EQUIPMENT_CHECKLIST_UNLINKED":
      return "Checklist unlinked from equipment";
    default:
      return String(entry?.action || "Checklist activity")
        .replace(/^EQUIPMENT_/, "")
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/^./, (char) => char.toUpperCase());
  }
};

const StatusBadge = ({ status, compact = false }) => {
  const meta = STATUS_META[status] || STATUS_META.incomplete;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ring-1 ring-inset ${meta.badge} ${
        compact ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"
      } font-semibold`}
    >
      <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const ProgressBar = ({ value }) => (
  <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
    <div
      className="h-full rounded-full bg-gray-900 transition-all"
      style={{ width: `${Math.max(0, Math.min(100, Number(value || 0)))}%` }}
    />
  </div>
);

const MetricCard = ({ icon: Icon, label, value, detail, danger }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p
          className={`mt-1 text-2xl font-bold ${danger ? "text-red-700" : "text-gray-950"}`}
        >
          {value}
        </p>
        {detail && <p className="mt-1 text-xs text-gray-500">{detail}</p>}
      </div>
      <div
        className={`rounded-xl p-2.5 ${danger ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-700"}`}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const Modal = ({ title, subtitle, onClose, children, wide = false }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-950/50 p-4">
    <div
      className={`max-h-[92vh] w-full overflow-hidden rounded-2xl bg-white shadow-2xl ${
        wide ? "max-w-4xl" : "max-w-xl"
      }`}
    >
      <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
        <div>
          <h3 className="text-lg font-bold text-gray-950">{title}</h3>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="max-h-[calc(92vh-82px)] overflow-y-auto p-6">
        {children}
      </div>
    </div>
  </div>
);

const EquipmentOverview = ({
  data,
  dimension,
  onDimensionChange,
  onOpenGroup,
}) => {
  const totals = data?.totals || {};
  const groups = data?.groups || [];
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={Wrench}
          label="Total equipment"
          value={totals.equipment || 0}
        />
        <MetricCard
          icon={Gauge}
          label="Current stage progress"
          value={`${totals.completion_percent || 0}%`}
          detail="Average completion of the current lifecycle checklist stage"
        />
        <MetricCard
          icon={MessageSquare}
          label="Open tasks"
          value={totals.open_tasks || 0}
        />
        <MetricCard
          icon={AlertCircle}
          label="Overdue tasks"
          value={totals.overdue_tasks || 0}
          danger={(totals.overdue_tasks || 0) > 0}
        />
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="font-bold text-gray-950">Lifecycle distribution</h3>
            <p className="text-sm text-gray-500">
              A single project-wide view of commissioning progress.
            </p>
          </div>
          <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
            {[
              ["room", "Room"],
              ["skid", "Skid"],
              ["classification", "Classification"],
              ["type", "Type"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => onDimensionChange(value)}
                className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  dimension === value
                    ? "bg-gray-950 text-white shadow-sm"
                    : "text-gray-600 hover:bg-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex h-10 overflow-hidden rounded-xl bg-gray-100">
          {STATUSES.map((status) => {
            const count = data?.status_distribution?.[status.id] || 0;
            const total = Math.max(1, totals.equipment || 0);
            const width = (count / total) * 100;
            const color = {
              incomplete: "bg-yellow-400",
              assembled: "bg-blue-500",
              installed: "bg-orange-500",
              pre_energization: "bg-green-500",
              energized_commissioned: "bg-purple-500",
            }[status.id];
            return width > 0 ? (
              <div
                key={status.id}
                className={`${color} flex items-center justify-center text-xs font-bold text-white`}
                style={{ width: `${width}%` }}
                title={`${status.label}: ${count}`}
              >
                {width >= 9 ? count : ""}
              </div>
            ) : null;
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
          {STATUSES.map((status) => (
            <div
              key={status.id}
              className="flex items-center gap-2 text-xs text-gray-600"
            >
              <span
                className={`h-2.5 w-2.5 rounded-full ${STATUS_META[status.id].dot}`}
              />
              <span>{status.label}</span>
              <strong className="text-gray-900">
                {data?.status_distribution?.[status.id] || 0}
              </strong>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {groups.map((group) => (
          <button
            type="button"
            key={group.key}
            onClick={() => onOpenGroup(group)}
            className="group rounded-2xl border border-gray-200 bg-white p-5 text-left transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-bold text-gray-950">
                  {group.label}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {group.equipment_count} equipment
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-300 transition group-hover:translate-x-1 group-hover:text-gray-700" />
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="font-semibold text-gray-700">
                Current stage progress
              </span>
              <span className="font-bold text-gray-950">
                {group.completion_percent}%
              </span>
            </div>
            <div className="mt-2">
              <ProgressBar value={group.completion_percent} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-center">
              <div className="rounded-xl bg-gray-50 p-2">
                <div className="text-sm font-bold text-gray-950">
                  {group.open_tasks}
                </div>
                <div className="text-[11px] text-gray-500">Open tasks</div>
              </div>
              <div
                className={`rounded-xl p-2 ${group.overdue_tasks ? "bg-red-50" : "bg-gray-50"}`}
              >
                <div
                  className={`text-sm font-bold ${group.overdue_tasks ? "text-red-700" : "text-gray-950"}`}
                >
                  {group.overdue_tasks}
                </div>
                <div className="text-[11px] text-gray-500">Overdue</div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {STATUSES.map((status) => {
                const count = group.status_distribution?.[status.id] || 0;
                return count ? (
                  <span
                    key={status.id}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ring-1 ring-inset ${STATUS_META[status.id].badge}`}
                  >
                    {count} {status.label}
                  </span>
                ) : null;
              })}
            </div>
          </button>
        ))}
      </div>

      {!groups.length && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <Wrench className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 font-semibold text-gray-700">
            No equipment has been created yet.
          </p>
        </div>
      )}
    </div>
  );
};

const EquipmentRegister = ({ items, options, onOpen, filters, setFilters }) => {
  const filtered = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return items.filter((item) => {
      if (filters.status !== "all" && item.status !== filters.status)
        return false;
      if (
        filters.classification !== "all" &&
        String(item.classification_id || item.equipment_type_id) !==
          filters.classification
      )
        return false;
      if (
        filters.type !== "all" &&
        String(item.type_id || "none") !== filters.type
      )
        return false;
      if (
        filters.room !== "all" &&
        String(item.room_id || "none") !== filters.room
      )
        return false;
      if (
        filters.skid !== "all" &&
        String(item.skid_id || "none") !== filters.skid
      )
        return false;
      if (!query) return true;
      return [
        item.equipment_number,
        item.equipment_tag,
        item.classification || item.equipment_type,
        item.type,
        item.room,
        item.skid,
        item.manufacturer,
        item.model_number,
        item.serial_number,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    }).sort(
      (left, right) =>
        (STATUS_ORDER[right.status] ?? 0) - (STATUS_ORDER[left.status] ?? 0),
    );
  }, [items, filters]);

  // Skids are project-wide options; constrain the filter to skids actually
  // associated with equipment in the selected room.
  const availableSkids = useMemo(() => {
    if (filters.room === "all") return options.skids || [];
    const usedSkidIds = new Set(
      items
        .filter((item) => String(item.room_id || "none") === filters.room)
        .map((item) => String(item.skid_id || "none")),
    );
    return (options.skids || []).filter((option) =>
      usedSkidIds.has(String(option.id)),
    );
  }, [items, options.skids, filters.room]);

  const hasUnassignedSkid =
    filters.room === "all" ||
    items.some(
      (item) =>
        String(item.room_id || "none") === filters.room && !item.skid_id,
    );

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 xl:flex-row">
          <label className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={filters.search}
              onChange={(event) =>
                setFilters((value) => ({
                  ...value,
                  search: event.target.value,
                }))
              }
              placeholder="Search number, tag, classification, type, room, skid, manufacturer..."
              className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            />
          </label>
          <select
            value={filters.status}
            onChange={(event) =>
              setFilters((value) => ({ ...value, status: event.target.value }))
            }
            className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((status) => (
              <option key={status.id} value={status.id}>
                {status.label}
              </option>
            ))}
          </select>
          <select
            value={filters.classification}
            onChange={(event) =>
              setFilters((value) => ({
                ...value,
                classification: event.target.value,
                type: "all",
              }))
            }
            className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
          >
            <option value="all">All classifications</option>
            {(options.classifications || options.types || []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
          <select
            value={filters.type}
            onChange={(event) =>
              setFilters((value) => ({ ...value, type: event.target.value }))
            }
            className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
          >
            <option value="all">All types</option>
            <option value="none">No specific type</option>
            {(options.specific_types || [])
              .filter(
                (option) =>
                  filters.classification === "all" ||
                  String(option.classification_id) === filters.classification,
              )
              .map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
          </select>
          <select
            value={filters.room}
            onChange={(event) =>
              setFilters((value) => ({
                ...value,
                room: event.target.value,
                skid: "all", // Reset a skid that may belong to a different room.
              }))
            }
            className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
          >
            <option value="all">All rooms</option>
            <option value="none">Unassigned room</option>
            {(options.rooms || []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
          <select
            value={filters.skid}
            onChange={(event) =>
              setFilters((value) => ({ ...value, skid: event.target.value }))
            }
            className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
          >
            <option value="all">All skids</option>
            {hasUnassignedSkid && (
              <option value="none">Unassigned skid</option>
            )}
            {availableSkids.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="font-semibold text-gray-900">Equipment Register</div>
          <div className="text-sm text-gray-500">{filtered.length} records</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-x-0 border-spacing-y-1 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Equipment</th>
                <th className="px-4 py-3">Classification</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Room / Skid</th>
                <th className="px-4 py-3">Documents</th>
                <th className="px-4 py-3">Tasks</th>
                <th className="px-4 py-3">Readiness</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr
                  key={item.id}
                  onClick={(event) => {
                    if (event.target.closest("button, a, input, select, textarea")) return;
                    onOpen(item.id);
                  }}
                  className={`cursor-pointer transition-colors ${STATUS_META[item.status]?.row || "bg-white hover:bg-gray-50"}`}
                >
                  <td
                    className={`border-l-4 px-4 py-3 ${STATUS_META[item.status]?.accent || "border-l-gray-300"}`}
                  >
                    <button
                      type="button"
                      onClick={() => onOpen(item.id)}
                      aria-label={`Open equipment ${item.equipment_tag || item.equipment_number}`}
                      className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                    >
                      <div className="font-bold text-gray-950">
                        {item.equipment_tag}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {item.equipment_number}
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {item.classification || item.equipment_type || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {item.type || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-800">{item.room || "—"}</div>
                    <div className="text-xs text-gray-500">
                      {item.skid || "No skid"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">
                      {item.accepted_document_count}/{item.document_count}
                    </div>
                    <div className="text-xs text-gray-500">
                      accepted / linked
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className={
                        item.overdue_task_count
                          ? "font-bold text-red-700"
                          : "font-semibold text-gray-900"
                      }
                    >
                      {item.open_task_count}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.overdue_task_count} overdue
                    </div>
                  </td>
                  <td className="min-w-[150px] px-4 py-3">
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-gray-500">Progress</span>
                      <strong>
                        {item.checklist_progress?.current_percent || 0}%
                      </strong>
                    </div>
                    <ProgressBar
                      value={item.checklist_progress?.current_percent}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onOpen(item.id)}
                      aria-label={`Open equipment ${item.equipment_tag || item.equipment_number}`}
                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-900"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!filtered.length && (
          <div className="p-10 text-center text-sm text-gray-500">
            No equipment matches the selected filters.
          </div>
        )}
      </div>
    </div>
  );
};

const TaskCard = ({ task, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(task)}
    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left transition hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-gray-950">{task.title}</span>
          {task.overdue && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">
              Overdue
            </span>
          )}
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
            {task.status?.replaceAll("_", " ")}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-1 text-xs text-gray-600">
          {task.question}
        </p>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span>{task.equipment_tag || task.equipment_number}</span>
          <span>Assigned to {task.assigned_to_name || "—"}</span>
          <span>Due {task.due_date || "not set"}</span>
        </div>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-gray-300" />
    </div>
  </button>
);

const TasksView = ({ tasks, onOpenTask, initialFilter = "open", actions = null }) => {
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState("");
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tasks.filter((task) => {
      if (filter === "overdue" && !task.overdue) return false;
      if (filter === "submitted" && task.status !== "submitted") return false;
      if (filter === "open" && ["verified", "closed"].includes(task.status)) return false;
      return !query || [task.title, task.question, task.equipment_tag,
        task.equipment_number, task.assigned_to_name, task.due_date,
        task.status?.replaceAll("_", " ")]
        .filter(Boolean).join(" ").toLowerCase().includes(query);
    });
  }, [tasks, filter, search]);
  return (
    <div className="space-y-2">
      <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-gray-950">Equipment Tasks <span className="font-normal text-gray-500">({visible.length} of {tasks.length})</span></h3>
          {actions}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative min-w-[180px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              aria-label="Search equipment tasks"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search task, equipment, assignee..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-orange-500 focus:outline-none"
            />
          </label>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {["open", "submitted", "overdue", "all"].map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={`rounded-md px-2.5 py-1.5 text-xs font-semibold capitalize ${filter === value ? "bg-white text-gray-950 shadow-sm" : "text-gray-500"}`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid gap-2 lg:grid-cols-2">
        {visible.map((task) => (
          <TaskCard key={task.id} task={task} onOpen={onOpenTask} />
        ))}
      </div>
      {!visible.length && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
          {search.trim() ? "No tasks match your search and filter." : "No tasks in this view."}
        </div>
      )}
    </div>
  );
};

const EquipmentDocumentsView = ({ documents, onOpenEquipment }) => {
  const [search, setSearch] = useState("");
  const [review, setReview] = useState("all");
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (documents || []).filter((doc) => {
      if (review !== "all" && doc.review_status !== review) return false;
      if (!query) return true;
      return [
        doc.document_name,
        doc.document_type,
        doc.equipment_number,
        doc.equipment_tag,
        doc.classification || doc.equipment_type,
        doc.type,
        doc.room,
        doc.skid,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [documents, search, review]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 lg:flex-row">
        <label className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search equipment documents..."
            className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm"
          />
        </label>
        <select
          value={review}
          onChange={(event) => setReview(event.target.value)}
          className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
        >
          <option value="all">All review statuses</option>
          <option value="pending">Pending review</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="font-bold text-gray-950">Equipment Documents</h3>
            <p className="text-sm text-gray-500">
              All documents linked to equipment records.
            </p>
          </div>
          <span className="text-sm text-gray-500">{visible.length} files</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Document</th>
                <th className="px-4 py-3">Equipment</th>
                <th className="px-4 py-3">Room / Skid</th>
                <th className="px-4 py-3">Lifecycle gate</th>
                <th className="px-4 py-3">Review</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-gray-950 hover:underline"
                    >
                      {doc.document_name}
                    </a>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {doc.document_type} · Rev {doc.version || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onOpenEquipment(doc.equipment_id)}
                      className="text-left"
                    >
                      <div className="font-semibold text-gray-900">
                        {doc.equipment_tag}
                      </div>
                      <div className="text-xs text-gray-500">
                        {doc.equipment_number} ·{" "}
                        {doc.classification || doc.equipment_type}
                        {doc.type ? ` → ${doc.type}` : ""}
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{doc.room || "—"}</div>
                    <div className="text-xs text-gray-500">
                      {doc.skid || "No skid"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {STATUS_META[doc.lifecycle_stage]?.label ||
                      doc.lifecycle_stage}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                        doc.review_status === "accepted"
                          ? "bg-green-100 text-green-700"
                          : doc.review_status === "rejected"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {doc.review_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onOpenEquipment(doc.equipment_id)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-900"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!visible.length && (
          <div className="p-10 text-center text-sm text-gray-500">
            No equipment documents match this view.
          </div>
        )}
      </div>
    </div>
  );
};

const CreateEquipmentModal = ({ options, onClose, onCreated, projectId }) => {
  const [form, setForm] = useState(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const classifications = options.classifications || options.types || [];
  const classificationMatch = classifications.find(
    (item) =>
      String(item.name || "")
        .trim()
        .toLowerCase() === form.classification_name.trim().toLowerCase(),
  );
  const availableTypes = classificationMatch
    ? (options.specific_types || []).filter(
        (item) =>
          Number(item.classification_id) === Number(classificationMatch.id),
      )
    : [];

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await equipmentAPI.create(projectId, {
        ...form,
        equipment_tag: form.equipment_tag.trim(),
        classification_name: form.classification_name.trim(),
        type_name: form.type_name.trim(),
        room_name: form.room_name.trim() || null,
        skid_name: form.skid_name.trim() || null,
      });
      onCreated(response.data);
    } catch (err) {
      setError(apiMessage(err, "Failed to create equipment."));
    } finally {
      setSaving(false);
    }
  };
  const field = (name, label, placeholder, list) => (
    <label className="block">
      <span className="text-sm font-semibold text-gray-700">{label}</span>
      <input
        list={list}
        value={form[name]}
        onChange={(event) =>
          setForm((value) => ({
            ...value,
            [name]: event.target.value,
            ...(name === "classification_name" ? { type_name: "" } : {}),
          }))
        }
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
      />
    </label>
  );
  return (
    <Modal
      title="Create New Equipment"
      subtitle="Classification is the broad family; Type is the specific equipment definition without location."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {field(
          "equipment_tag",
          "Equipment tag / specific name",
          "e.g. PP-L2-01",
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          {field(
            "classification_name",
            "Classification",
            "e.g. Panelboard, LV Switchboard",
            "equipment-classification-options",
          )}
          {field(
            "type_name",
            "Type",
            "e.g. 480V Panelboard Type A",
            "equipment-specific-type-options",
          )}
          <datalist id="equipment-classification-options">
            {classifications.map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
          <datalist id="equipment-specific-type-options">
            {availableTypes.map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
        </div>
        <p className="-mt-2 text-xs text-gray-500">
          A published default checklist for the selected Type will be assigned
          automatically when available.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {field(
            "room_name",
            "Room",
            "Select or type a new room",
            "equipment-room-options",
          )}
          {field(
            "skid_name",
            "Skid",
            "Select or type a new skid",
            "equipment-skid-options",
          )}
          <datalist id="equipment-room-options">
            {(options.rooms || []).map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
          <datalist id="equipment-skid-options">
            {(options.skids || []).map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {field("manufacturer", "Manufacturer", "Optional")}
          {field("model_number", "Model", "Optional")}
          {field("serial_number", "Serial number", "Optional")}
        </div>
        <label className="block">
          <span className="text-sm font-semibold text-gray-700">
            Description
          </span>
          <textarea
            value={form.description}
            onChange={(event) =>
              setForm((value) => ({
                ...value,
                description: event.target.value,
              }))
            }
            rows={3}
            className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              saving ||
              !form.equipment_tag.trim() ||
              !form.classification_name.trim() ||
              !form.type_name.trim()
            }
            className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Equipment
          </button>
        </div>
      </form>
    </Modal>
  );
};

const EditEquipmentModal = ({
  options,
  equipment,
  onClose,
  onSaved,
  projectId,
}) => {
  const [form, setForm] = useState({
    equipment_tag: equipment.equipment_tag || "",
    classification_name:
      equipment.classification || equipment.equipment_type || "",
    type_name: equipment.type || "",
    room_name: equipment.room || "",
    skid_name: equipment.skid || "",
    manufacturer: equipment.manufacturer || "",
    model_number: equipment.model_number || "",
    serial_number: equipment.serial_number || "",
    description: equipment.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const classifications = options.classifications || options.types || [];
  const classificationMatch = classifications.find(
    (item) =>
      String(item.name || "")
        .trim()
        .toLowerCase() === form.classification_name.trim().toLowerCase(),
  );
  const availableTypes = classificationMatch
    ? (options.specific_types || []).filter(
        (item) =>
          Number(item.classification_id) === Number(classificationMatch.id),
      )
    : [];
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await equipmentAPI.update(projectId, equipment.id, {
        equipment_tag: form.equipment_tag.trim(),
        classification_name: form.classification_name.trim(),
        type_name: form.type_name.trim() || null,
        clear_type: !form.type_name.trim(),
        room_name: form.room_name.trim() || null,
        clear_room: !form.room_name.trim(),
        skid_name: form.skid_name.trim() || null,
        clear_skid: !form.skid_name.trim(),
        manufacturer: form.manufacturer.trim() || null,
        model_number: form.model_number.trim() || null,
        serial_number: form.serial_number.trim() || null,
        description: form.description.trim() || null,
      });
      onSaved(response.data);
    } catch (err) {
      setError(apiMessage(err, "Failed to update equipment."));
    } finally {
      setSaving(false);
    }
  };
  const input = (name, label, list) => (
    <label className="block text-sm font-semibold text-gray-700">
      {label}
      <input
        list={list}
        value={form[name]}
        onChange={(event) =>
          setForm((value) => ({
            ...value,
            [name]: event.target.value,
            ...(name === "classification_name" ? { type_name: "" } : {}),
          }))
        }
        className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 font-normal"
      />
    </label>
  );
  return (
    <Modal
      title="Edit Equipment"
      subtitle={`${equipment.equipment_number} · ${equipment.equipment_tag}`}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {input("equipment_tag", "Equipment tag")}
        <div className="grid gap-4 sm:grid-cols-2">
          {input(
            "classification_name",
            "Classification",
            "edit-equipment-classifications",
          )}
          {input("type_name", "Type", "edit-equipment-specific-types")}
          <datalist id="edit-equipment-classifications">
            {classifications.map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
          <datalist id="edit-equipment-specific-types">
            {availableTypes.map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
        </div>
        <p className="-mt-2 text-xs text-gray-500">
          Changing Type never replaces an active checklist. If none is assigned,
          the published default for the new Type is auto-assigned.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {input("room_name", "Room", "edit-equipment-rooms")}
          {input("skid_name", "Skid", "edit-equipment-skids")}
          <datalist id="edit-equipment-rooms">
            {(options.rooms || []).map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
          <datalist id="edit-equipment-skids">
            {(options.skids || []).map((option) => (
              <option key={option.id} value={option.name} />
            ))}
          </datalist>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {input("manufacturer", "Manufacturer")}
          {input("model_number", "Model")}
          {input("serial_number", "Serial number")}
        </div>
        <label className="block text-sm font-semibold text-gray-700">
          Description
          <textarea
            value={form.description}
            onChange={(event) =>
              setForm((value) => ({
                ...value,
                description: event.target.value,
              }))
            }
            rows={3}
            className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 font-normal"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border px-4 py-2.5 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            disabled={
              saving ||
              !form.equipment_tag.trim() ||
              !form.classification_name.trim()
            }
            className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            changes
          </button>
        </div>
      </form>
    </Modal>
  );
};

const TaskModal = ({
  projectId,
  equipment,
  members,
  initial,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState({ ...EMPTY_TASK, ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const membersByRole = useMemo(() => {
    const groups = new Map();
    (members || [])
      .filter((member) => member.is_active !== false)
      .forEach((member) => {
        const role = String(member.role || "other").toLowerCase();
        if (!groups.has(role)) groups.set(role, []);
        groups.get(role).push(member);
      });
    return [...groups.entries()]
      .sort(([left], [right]) =>
        roleDisplayLabel(left).localeCompare(roleDisplayLabel(right)),
      )
      .map(([role, people]) => ({
        role,
        label: roleDisplayLabel(role),
        people: people.sort((left, right) => {
          const leftName =
            `${left.first_name || ""} ${left.last_name || ""}`.trim();
          const rightName =
            `${right.first_name || ""} ${right.last_name || ""}`.trim();
          return leftName.localeCompare(rightName, undefined, {
            sensitivity: "base",
          });
        }),
      }));
  }, [members]);
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await equipmentAPI.createTask(projectId, equipment.id, {
        ...form,
        assigned_to_user_id: Number(form.assigned_to_user_id),
        due_date: form.due_date || null,
      });
      onSaved(response.data);
    } catch (err) {
      setError(apiMessage(err, "Failed to assign equipment task."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      title="Assign Equipment Task"
      subtitle={`${equipment.equipment_tag} · Ask a question or assign field/documentation work.`}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <label className="block text-sm font-semibold text-gray-700">
          Title
          <input
            value={form.title}
            onChange={(event) =>
              setForm((value) => ({ ...value, title: event.target.value }))
            }
            className="mt-1.5 w-full rounded-xl border px-3 py-2.5 font-normal"
          />
        </label>
        <label className="block text-sm font-semibold text-gray-700">
          Question / required action
          <textarea
            value={form.question}
            onChange={(event) =>
              setForm((value) => ({ ...value, question: event.target.value }))
            }
            rows={4}
            className="mt-1.5 w-full rounded-xl border px-3 py-2.5 font-normal"
          />
        </label>
        <label className="block text-sm font-semibold text-gray-700">
          Instructions
          <textarea
            value={form.instructions}
            onChange={(event) =>
              setForm((value) => ({
                ...value,
                instructions: event.target.value,
              }))
            }
            rows={2}
            className="mt-1.5 w-full rounded-xl border px-3 py-2.5 font-normal"
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold text-gray-700">
            Task type
            <select
              value={form.task_type}
              onChange={(event) =>
                setForm((value) => ({
                  ...value,
                  task_type: event.target.value,
                }))
              }
              className="mt-1.5 w-full rounded-xl border px-3 py-2.5 font-normal"
            >
              {TASK_TYPES.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-gray-700">
            Lifecycle stage
            <select
              value={form.lifecycle_stage}
              onChange={(event) =>
                setForm((value) => ({
                  ...value,
                  lifecycle_stage: event.target.value,
                }))
              }
              className="mt-1.5 w-full rounded-xl border px-3 py-2.5 font-normal"
            >
              {STATUSES.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-gray-700">
            Assigned person
            <select
              value={form.assigned_to_user_id}
              onChange={(event) =>
                setForm((value) => ({
                  ...value,
                  assigned_to_user_id: event.target.value,
                }))
              }
              className="mt-1.5 w-full rounded-xl border px-3 py-2.5 font-normal"
            >
              <option value="">Select project member</option>
              {membersByRole.map((group) => (
                <optgroup key={group.role} label={group.label}>
                  {group.people.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.first_name} {member.last_name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-gray-700">
            Due date
            <div className="mt-1.5 flex w-full min-w-0 rounded-xl border px-3 py-2.5">
              <input
                type="date"
                value={form.due_date}
                onChange={(event) =>
                  setForm((value) => ({
                    ...value,
                    due_date: event.target.value,
                  }))
                }
                className="block w-full min-w-0 border-0 bg-transparent p-0 font-normal"
              />
            </div>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border px-4 py-2.5 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            disabled={
              saving ||
              !form.title.trim() ||
              !form.question.trim() ||
              !form.assigned_to_user_id
            }
            className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Assign Task
          </button>
        </div>
      </form>
    </Modal>
  );
};

const TaskDetailModal = ({
  projectId,
  taskId,
  currentUser,
  canVerify,
  onClose,
  onChanged,
}) => {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState("");
  const [comment, setComment] = useState("");
  const [commentKind, setCommentKind] = useState("comment");
  const [evidence, setEvidence] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await equipmentAPI.getTask(projectId, taskId);
      setTask(res.data);
      setResponse(res.data?.response_text || "");
    } catch (err) {
      setError(apiMessage(err, "Failed to load task."));
    } finally {
      setLoading(false);
    }
  }, [projectId, taskId]);
  useEffect(() => {
    load();
  }, [load]);
  const action = async (name, fn) => {
    setBusy(name);
    setError("");
    try {
      const result = await fn();
      setTask(result.data);
      onChanged?.();
    } catch (err) {
      setError(apiMessage(err, `Failed to ${name}.`));
    } finally {
      setBusy("");
    }
  };
  const isAssignee =
    Number(task?.assigned_to_user_id) === Number(currentUser?.id);
  return (
    <Modal
      title={task?.title || "Equipment Task"}
      subtitle={
        task
          ? `${task.equipment_tag || task.equipment_number} · ${task.status?.replaceAll("_", " ")}`
          : ""
      }
      onClose={onClose}
      wide
    >
      {loading ? (
        <div className="flex justify-center p-12">
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="grid gap-4 lg:grid-cols-[1.2fr_.8fr]">
            <div className="rounded-2xl border border-gray-200 p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                Question / required action
              </p>
              <p className="mt-2 whitespace-pre-wrap text-gray-900">
                {task?.question}
              </p>
              {task?.instructions && (
                <p className="mt-3 whitespace-pre-wrap rounded-xl bg-gray-50 p-3 text-sm text-gray-600">
                  {task.instructions}
                </p>
              )}
            </div>
            <div className="rounded-2xl bg-gray-950 p-4 text-white">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-gray-400">Assigned to</div>
                  <div className="mt-1 font-semibold">
                    {task?.assigned_to_name}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Due</div>
                  <div className="mt-1 font-semibold">
                    {task?.due_date || "Not set"}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">Type</div>
                  <div className="mt-1 font-semibold">{task?.task_type}</div>
                </div>
                <div>
                  <div className="text-gray-400">Priority</div>
                  <div className="mt-1 font-semibold capitalize">
                    {task?.priority}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(isAssignee || currentUser?.role === "admin") &&
            !["verified", "closed"].includes(task?.status) && (
              <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
                <h4 className="font-bold text-gray-950">Submit response</h4>
                <textarea
                  value={response}
                  onChange={(event) => setResponse(event.target.value)}
                  rows={4}
                  className="mt-3 w-full rounded-xl border border-orange-200 bg-white px-3 py-2.5"
                  placeholder="Explain the work completed or answer the question..."
                />
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700">
                    <Upload className="h-4 w-4" />
                    <span>{evidence?.name || "Attach evidence"}</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(event) =>
                        setEvidence(event.target.files?.[0] || null)
                      }
                    />
                  </label>
                  <button
                    type="button"
                    disabled={!response.trim() || busy}
                    onClick={() =>
                      action("submit response", async () => {
                        if (evidence) {
                          const fd = new FormData();
                          fd.append("file", evidence);
                          await equipmentAPI.uploadTaskEvidence(
                            projectId,
                            taskId,
                            fd,
                          );
                        }
                        return equipmentAPI.respondTask(projectId, taskId, {
                          response_text: response,
                        });
                      })
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{" "}
                    <Send className="h-4 w-4" /> Submit for verification
                  </button>
                </div>
              </div>
            )}

          {canVerify &&
            ["submitted", "verified", "closed"].includes(task?.status) && (
              <div className="flex flex-wrap gap-2 rounded-2xl border border-gray-200 p-4">
                {!["verified", "closed"].includes(task.status) ? (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        action("verify", () =>
                          equipmentAPI.decideTask(projectId, taskId, {
                            action: "verify",
                            note: "Evidence reviewed and accepted.",
                          }),
                        )
                      }
                      className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-semibold text-white"
                    >
                      <CheckCircle2 className="h-4 w-4" /> Verify
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const note = window.prompt(
                          "Reason for rejection or clarification:",
                        );
                        if (note)
                          action("reject", () =>
                            equipmentAPI.decideTask(projectId, taskId, {
                              action: "reject",
                              note,
                            }),
                          );
                      }}
                      className="rounded-xl border border-red-300 px-4 py-2.5 text-sm font-semibold text-red-700"
                    >
                      Reject / Clarify
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      action("reopen", () =>
                        equipmentAPI.decideTask(projectId, taskId, {
                          action: "reopen",
                          note: "Task reopened for additional work.",
                        }),
                      )
                    }
                    className="rounded-xl border px-4 py-2.5 text-sm font-semibold"
                  >
                    Reopen task
                  </button>
                )}
              </div>
            )}

          <div className="grid gap-5 lg:grid-cols-[1.2fr_.8fr]">
            <div>
              <h4 className="font-bold text-gray-950">
                Question & response thread
              </h4>
              <div className="mt-3 space-y-3">
                {(task?.comments || []).map((entry) => (
                  <div
                    key={entry.id}
                    className={`rounded-2xl border p-4 ${entry.kind === "response" ? "border-green-200 bg-green-50" : entry.kind === "question" ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-white"}`}
                  >
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="font-bold uppercase tracking-wide text-gray-600">
                        {entry.kind}
                      </span>
                      <span className="text-gray-500">{entry.author_name}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">
                      {entry.body}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-[170px_1fr_auto]">
                <select
                  value={commentKind}
                  onChange={(event) => setCommentKind(event.target.value)}
                  className="rounded-xl border px-3 py-2.5 text-sm"
                >
                  <option value="comment">Comment</option>
                  <option value="question">Follow-up question</option>
                  <option value="response">Response note</option>
                </select>
                <input
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Write a follow-up or comment..."
                  className="rounded-xl border px-3 py-2.5 text-sm"
                />
                <button
                  type="button"
                  disabled={!comment.trim() || busy}
                  onClick={() =>
                    action("add comment", async () => {
                      const res = await equipmentAPI.addTaskComment(
                        projectId,
                        taskId,
                        { body: comment, kind: commentKind },
                      );
                      setComment("");
                      setCommentKind("comment");
                      return res;
                    })
                  }
                  className="rounded-xl bg-gray-950 px-4 py-2.5 text-white disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-gray-950">Evidence</h4>
              <div className="mt-3 space-y-2">
                {(task?.evidence || []).map((file) => (
                  <a
                    key={file.id}
                    href={file.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 hover:bg-gray-50"
                  >
                    <FileText className="h-5 w-5 text-gray-500" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {file.document_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Uploaded by {file.linked_by_name || "—"}
                      </div>
                    </div>
                  </a>
                ))}
                {!task?.evidence?.length && (
                  <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">
                    No evidence uploaded yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

const EquipmentDrawer = ({
  projectId,
  equipmentId,
  currentUser,
  projectRole,
  members,
  options,
  onClose,
  onChanged,
  initialTaskId,
}) => {
  const [detail, setDetail] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [checklistActivity, setChecklistActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [tab, setTab] = useState(initialTaskId ? "tasks" : "checklist");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showTask, setShowTask] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [taskInitial, setTaskInitial] = useState(null);
  const [openTaskId, setOpenTaskId] = useState(initialTaskId || null);
  const [busy, setBusy] = useState("");
  const [closingDrawer, setClosingDrawer] = useState(false);
  const checklistEditStateRef = useRef({
    save: null,
    discard: null,
    dirty: false,
    saving: false,
    lastSavedAt: null,
  });
  const checklistChangedSinceRefreshRef = useRef(false);

  const admin = isSystemAdmin(currentUser?.role);
  const isExternal = currentUser?.role === "user_external";
  const role = (projectRole || "").toLowerCase();
  const canManage =
    admin ||
    (!isExternal &&
      ["project_manager", "qa_qc_manager", "inspector"].includes(role));
  const canReview = canManage;
  const canAssign = admin || role === "qa_qc_manager";
  const canConfigureChecklists = admin || role === "qa_qc_manager";
  const canNotifyCompletion = admin || role === "qa_qc_manager";

  const load = useCallback(async () => {
    if (!projectId || !equipmentId) return;
    setLoading(true);
    setError("");
    try {
      const [detailRes, docsRes, taskRes] = await Promise.all([
        equipmentAPI.getOne(projectId, equipmentId),
        equipmentAPI.listDocuments(projectId, equipmentId),
        equipmentAPI.listEquipmentTasks(projectId, equipmentId),
      ]);
      setDetail(detailRes.data);
      setDocuments(docsRes.data || []);
      setTasks(taskRes.data || []);
    } catch (err) {
      setError(apiMessage(err, "Failed to load equipment."));
    } finally {
      setLoading(false);
    }
  }, [projectId, equipmentId]);
  useEffect(() => {
    load();
  }, [load]);

  const loadActivity = useCallback(async () => {
    if (!projectId || !equipmentId) return;
    setActivityLoading(true);
    try {
      const response = await equipmentAPI.listEquipmentChecklistActivity(
        projectId,
        equipmentId,
        200,
      );
      setChecklistActivity(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.warn("Failed to load Equipment checklist activity", err);
      setChecklistActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, [projectId, equipmentId]);

  useEffect(() => {
    if (tab === "activity") loadActivity();
  }, [tab, loadActivity]);

  const refreshEquipmentDetailSilently = useCallback(async () => {
    if (!projectId || !equipmentId) return;
    try {
      const response = await equipmentAPI.getOne(projectId, equipmentId);
      setDetail(response.data);
    } catch (err) {
      console.warn(
        "Failed to refresh Equipment readiness after checklist autosave",
        err,
      );
    }
  }, [projectId, equipmentId]);

  useEffect(() => {
    if (initialTaskId) {
      setTab("tasks");
      setOpenTaskId(initialTaskId);
    }
  }, [initialTaskId]);

  // Keep the Equipment page completely still while the drawer is open.
  // The drawer body below owns its own scroll container, so wheel/touch
  // scrolling never chains into the page behind it. Restore the exact page
  // position and any pre-existing inline styles when the drawer closes.
  useEffect(() => {
    const body = document.body;
    const root = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const previousBody = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };
    const previousRootOverflow = root.style.overflow;
    const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);

    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBody.overflow;
      body.style.position = previousBody.position;
      body.style.top = previousBody.top;
      body.style.width = previousBody.width;
      body.style.paddingRight = previousBody.paddingRight;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const run = async (name, fn) => {
    setBusy(name);
    setError("");
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiMessage(err, `Failed to ${name}.`));
    } finally {
      setBusy("");
    }
  };

  const reportChecklistEditState = useCallback((next) => {
    const previous = checklistEditStateRef.current || {};
    if (next?.lastSavedAt && next.lastSavedAt !== previous.lastSavedAt) {
      checklistChangedSinceRefreshRef.current = true;
    }
    checklistEditStateRef.current = next || {
      save: null,
      discard: null,
      dirty: false,
      saving: false,
      lastSavedAt: null,
    };
  }, []);

  const flushChecklistAutosave = useCallback(async () => {
    const state = checklistEditStateRef.current;
    if (!state?.dirty && !state?.saving) return true;
    if (!state?.save) return true;
    const okay = await state.save();
    if (okay !== false) return true;
    // A failed save must not trap the user behind the full-screen drawer.
    // Never discard silently: give a choice to stay and retry instead.
    const discard = window.confirm(
      "Checklist changes could not be saved. Discard the unsaved changes and continue?\n\nChoose Cancel to stay on the checklist and retry saving.",
    );
    if (!discard) return false;
    checklistEditStateRef.current?.discard?.();
    return true;
  }, []);

  const requestDrawerTab = useCallback(
    async (nextTab) => {
      if (nextTab === tab) return;
      let refreshAfterSwitch = false;
      if (tab === "checklist") {
        const okay = await flushChecklistAutosave();
        if (!okay) return;
        refreshAfterSwitch = checklistChangedSinceRefreshRef.current;
        checklistChangedSinceRefreshRef.current = false;
      }
      setTab(nextTab);
      if (refreshAfterSwitch) refreshEquipmentDetailSilently();
    },
    [flushChecklistAutosave, refreshEquipmentDetailSilently, tab],
  );

  const requestCloseDrawer = useCallback(async () => {
    if (closingDrawer) return;
    setClosingDrawer(true);
    try {
      const okay = await flushChecklistAutosave();
      if (!okay) return;
      const shouldRefreshParent = checklistChangedSinceRefreshRef.current;
      checklistChangedSinceRefreshRef.current = false;
      onClose();
      if (shouldRefreshParent) onChanged?.();
    } finally {
      setClosingDrawer(false);
    }
  }, [closingDrawer, flushChecklistAutosave, onChanged, onClose]);

  if (!equipmentId) return null;
  const readiness = detail?.readiness || {};
  const nextStatus = readiness.next_status;

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden bg-gray-950/40"
      onClick={(event) => {
        // Close only when the backdrop itself is clicked, not drawer content
        // or any nested modal. Preserve the existing checklist-save guard.
        if (event.target === event.currentTarget) requestCloseDrawer();
      }}
    >
      <div className="absolute inset-y-0 right-0 flex min-h-0 w-full max-w-6xl flex-col overflow-hidden bg-gray-50 shadow-2xl">
        <div className="shrink-0 border-b border-gray-200 bg-white px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold text-gray-950">
                  {detail?.equipment_tag || "Equipment"}
                </h2>
                {detail && <StatusBadge status={detail.status} />}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {detail?.equipment_number} ·{" "}
                {detail?.classification || detail?.equipment_type} ·{" "}
                {detail?.type || "No specific type"} ·{" "}
                {detail?.room || "No room"} · {detail?.skid || "No skid"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {canManage && (
                <button
                  type="button"
                  onClick={() => setShowEdit(true)}
                  className="rounded-xl border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Edit details
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowHelp(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <HelpCircle className="h-4 w-4" /> Help
              </button>
              <button
                type="button"
                disabled={closingDrawer}
                onClick={requestCloseDrawer}
                className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                {closingDrawer ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <X className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
          <div className="mt-4 flex gap-1 overflow-x-auto rounded-xl bg-gray-100 p-1">
            {[
              ["checklist", "Checklist", ListChecks],
              ["documents", "Document View", FileText],
              ["tasks", "Tasks", MessageSquare],
              ["activity", "Activity", Layers3],
            ].map(([value, label, Icon]) => (
              <button
                key={value}
                type="button"
                onClick={() => requestDrawerTab(value)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${tab === value ? "bg-white text-gray-950 shadow-sm" : "text-gray-500"}`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5 [scrollbar-gutter:stable]">
          {loading ? (
            <div className="flex justify-center p-20">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="mx-auto max-w-5xl space-y-5">
              {error && (
                <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              {tab === "documents" && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-gray-200 bg-white p-4">
                    <h3 className="font-bold text-gray-950">
                      Equipment Document View
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Documents are uploaded from their checklist requirement.
                      This view provides equipment-level traceability and
                      retains older legacy document links as read-only history.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="rounded-xl bg-gray-100 p-2.5">
                            <FileText className="h-5 w-5 text-gray-600" />
                          </div>
                          <div className="min-w-0">
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="truncate font-bold text-gray-950 hover:underline"
                            >
                              {doc.document_name}
                            </a>
                            <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                              {doc.checklist_item && (
                                <span>Checklist: {doc.checklist_item}</span>
                              )}
                              {doc.checklist_section && (
                                <span>Part: {doc.checklist_section}</span>
                              )}
                              {doc.lifecycle_stage && (
                                <span>
                                  {STATUS_META[doc.lifecycle_stage]?.label ||
                                    doc.lifecycle_stage}
                                </span>
                              )}
                              <span>
                                Uploaded by {doc.linked_by_name || "—"}
                              </span>
                              {doc.linked_at && (
                                <span>
                                  {new Date(doc.linked_at).toLocaleString()}
                                </span>
                              )}
                              {doc.source === "legacy" && (
                                <span className="font-semibold text-gray-600">
                                  Legacy
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className="self-start rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700">
                          Uploaded
                        </span>
                      </div>
                    ))}
                    {!documents.length && (
                      <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-gray-500">
                        No checklist documents have been uploaded for this
                        equipment.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tab === "checklist" && (
                <div className="space-y-5">
                  <DesignedChecklistPanel
                    projectId={projectId}
                    equipment={detail}
                    canFill={true}
                    canAssign={canAssign}
                    canConfigure={canConfigureChecklists}
                    canNotifyCompletion={canNotifyCompletion}
                    currentUserId={currentUser?.id}
                    onCreateTask={(initial) => {
                      setTaskInitial(initial);
                      setShowTask(true);
                    }}
                    onChanged={() => {
                      load();
                      onChanged?.();
                    }}
                    onEditStateChange={reportChecklistEditState}
                  />
                </div>
              )}

              {tab === "tasks" && (
                <TasksView
                  tasks={tasks}
                  initialFilter="all"
                  onOpenTask={(task) => setOpenTaskId(task.id)}
                  actions={canAssign ? (
                    <button
                      type="button"
                      onClick={() => {
                        setTaskInitial(null);
                        setShowTask(true);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-gray-950 px-3 py-2 text-xs font-semibold text-white"
                    >
                      <Plus className="h-3.5 w-3.5" /> Assign task
                    </button>
                  ) : null}
                />
              )}

              {tab === "activity" && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-gray-950">
                        Equipment activity
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Checklist edits, document uploads/removals, task
                        assignment and lifecycle changes.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={loadActivity}
                      disabled={activityLoading}
                      className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold text-gray-600 disabled:opacity-50"
                    >
                      <RefreshCw
                        className={`h-3.5 w-3.5 ${activityLoading ? "animate-spin" : ""}`}
                      />{" "}
                      Refresh
                    </button>
                  </div>
                  <div className="mt-5 space-y-4">
                    {[
                      ...(detail?.status_history || []).map((entry) => ({
                        key: `status-${entry.id}`,
                        created_at: entry.created_at,
                        actor_name: entry.changed_by_name || "System",
                        title: `${entry.from_status ? `${STATUS_META[entry.from_status]?.label || entry.from_status} → ` : ""}${STATUS_META[entry.to_status]?.label || entry.to_status}`,
                        detail: entry.reason || "Lifecycle recalculated.",
                        dot: STATUS_META[entry.to_status]?.dot || "bg-gray-400",
                      })),
                      ...checklistActivity.map((entry) => ({
                        key: `audit-${entry.id}`,
                        created_at: entry.created_at,
                        actor_name: entry.actor_name || "System",
                        title: equipmentActivityLabel(entry),
                        detail:
                          entry.action === "EQUIPMENT_CHECKLIST_FIELDS_SAVED" &&
                          entry.metadata?.changes?.length
                            ? entry.metadata.changes
                                .slice(0, 3)
                                .map(
                                  (change) =>
                                    `${change.section_title || "Checklist"}: ${change.field_label || change.row_label || "field"}`,
                                )
                                .join(" · ")
                            : entry.metadata?.lifecycle_stage
                              ? STATUS_META[entry.metadata.lifecycle_stage]
                                  ?.label || entry.metadata.lifecycle_stage
                              : "",
                        dot: "bg-blue-500",
                      })),
                    ]
                      .sort(
                        (left, right) =>
                          new Date(right.created_at || 0) -
                          new Date(left.created_at || 0),
                      )
                      .map((entry) => (
                        <div key={entry.key} className="flex gap-4">
                          <div
                            className={`mt-1 h-3 w-3 shrink-0 rounded-full ${entry.dot}`}
                          />
                          <div className="flex-1 border-b border-gray-100 pb-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-gray-900">
                                {entry.title}
                              </span>
                              <span className="text-xs text-gray-500">
                                {entry.actor_name}
                              </span>
                            </div>
                            {entry.detail && (
                              <p className="mt-1 text-sm text-gray-600">
                                {entry.detail}
                              </p>
                            )}
                            <p className="mt-1 text-xs text-gray-400">
                              {entry.created_at
                                ? new Date(entry.created_at).toLocaleString()
                                : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    {!activityLoading &&
                      !(detail?.status_history || []).length &&
                      !checklistActivity.length && (
                        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-gray-500">
                          No Equipment activity recorded yet.
                        </div>
                      )}
                    {activityLoading && !checklistActivity.length && (
                      <div className="flex justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showHelp && (
        <Modal
          title="Equipment workflow help"
          subtitle="How checklist-driven lifecycle and documents work"
          onClose={() => setShowHelp(false)}
        >
          <div className="space-y-4 text-sm text-gray-700">
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-bold text-gray-950">1. Lifecycle flow</div>
              <p className="mt-1">
                The Equipment register uses the fixed lifecycle statuses shown
                in the Equipment tab: Document Review and Arrival, Assembled /
                Ready for Delivery, Installed, Pre-Energization Complete, and
                Energized &amp; Commissioned. Checklist templates use these same
                stages rather than creating a second lifecycle.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-bold text-gray-950">
                2. Checklist document fields
              </div>
              <p className="mt-1">
                Designers add generic{" "}
                <strong>Document Upload / Attachment</strong> fields and may
                label them Test Report, Torque Report, Megger Report or any
                other required record. Uploads happen on the checklist and are
                collected in the Equipment Document View.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-bold text-gray-950">
                3. Stage-linked checklists
              </div>
              <p className="mt-1">
                Admin or QA/QC builds reusable Parts and Other blocks, then
                assigns each block to a fixed Equipment lifecycle stage. Other
                blocks can map to that stage and count toward completion, or use{" "}
                <strong>None</strong> to remain informational. Active Equipment
                receives later published template changes without overwriting
                prior answers.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-bold text-gray-950">
                4. Tasks and attachments
              </div>
              <p className="mt-1">
                A checklist question can create a task for an internal or
                external assignee. Designers may add one or more response fields
                such as a custom dropdown, comment, checkbox, text or
                attachment. User and date/time are recorded automatically by
                SmokyQ.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-bold text-gray-950">
                5. Automatic lifecycle
              </div>
              <p className="mt-1">
                All lifecycle stages stay open for permitted users to work
                ahead. The Equipment lifecycle is the earliest incomplete
                required checklist stage and recalculates automatically after
                checklist changes, including rollback when an earlier published
                stage gains a new incomplete requirement.
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 text-gray-600">
              Checklist completion is the lifecycle source of truth. Tasks and
              legacy Equipment document gates do not independently advance or
              block lifecycle status.
            </div>
          </div>
        </Modal>
      )}
      {showEdit && detail && (
        <EditEquipmentModal
          projectId={projectId}
          equipment={detail}
          options={options || { types: [], rooms: [], skids: [] }}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            load();
            onChanged?.();
          }}
        />
      )}
      {showTask && detail && (
        <TaskModal
          projectId={projectId}
          equipment={detail}
          members={members}
          initial={taskInitial}
          onClose={() => setShowTask(false)}
          onSaved={() => {
            setShowTask(false);
            load();
            onChanged?.();
          }}
        />
      )}
      {openTaskId && (
        <TaskDetailModal
          projectId={projectId}
          taskId={openTaskId}
          currentUser={currentUser}
          canVerify={canReview}
          onClose={() => setOpenTaskId(null)}
          onChanged={() => {
            load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
};

export default function EquipmentView({
  openEquipmentId = null,
  openTaskId = null,
  onOpenedContext,
}) {
  const { activeProject, currentUser, projectRole, projectMembers } =
    useProjectContext();
  const { projectId, items, overview, tasks, options, loading, error, reload } =
    useEquipment();
  const [activeTab, setActiveTab] = useState("overview");
  const [dimension, setDimension] = useState("room");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedEquipmentId, setSelectedEquipmentId] =
    useState(openEquipmentId);
  const [selectedTaskId, setSelectedTaskId] = useState(openTaskId);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    classification: "all",
    type: "all",
    room: "all",
    skid: "all",
  });

  const admin = isSystemAdmin(currentUser?.role);
  const isExternal = currentUser?.role === "user_external";
  const role = (projectRole || "").toLowerCase();
  const canCreate =
    admin ||
    isProjectEngineer(role) ||
    (!isExternal &&
      (isProjectManager(role) || isQaQcManager(role) || isInspector(role)));
  const canDesignTemplates = admin || isQaQcManager(role);

  useEffect(() => {
    if (openEquipmentId) {
      setSelectedEquipmentId(Number(openEquipmentId));
      setSelectedTaskId(openTaskId ? Number(openTaskId) : null);
      onOpenedContext?.();
    }
  }, [openEquipmentId, openTaskId, onOpenedContext]);

  const changeDimension = async (value) => {
    setDimension(value);
    await reload({ dimension: value });
  };
  const openGroup = (group) => {
    setActiveTab("register");
    if (dimension === "room")
      setFilters((value) => ({
        ...value,
        room: String(group.id || "none"),
        skid: "all",
      }));
    if (dimension === "skid")
      setFilters((value) => ({ ...value, skid: String(group.id || "none") }));
    if (dimension === "classification")
      setFilters((value) => ({
        ...value,
        classification: String(group.id || "all"),
        type: "all",
      }));
    if (dimension === "type")
      setFilters((value) => ({ ...value, type: String(group.id || "none") }));
  };

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-3xl bg-gray-950 text-white">
        <div className="flex flex-col gap-5 p-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-orange-300">
              <Wrench className="h-4 w-4" /> Equipment Tracking
            </div>
            <h1 className="mt-2 text-2xl font-bold">
              Commissioning readiness, one equipment record at a time
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-300">
              Manage equipment, structured checklists, checklist documents,
              assigned questions and automatic lifecycle progress for{" "}
              {activeProject?.name}.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => reload({ dimension })}
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold hover:bg-white/10"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />{" "}
              Refresh
            </button>
            {canCreate && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-600"
              >
                <Plus className="h-4 w-4" /> New Equipment
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-1 overflow-x-auto border-t border-white/10 px-4 pt-3">
          {[
            ["overview", "Overview", Gauge],
            ["register", "Equipment Register", Wrench],
            ["tasks", "Tasks", MessageSquare],
            ...(canDesignTemplates
              ? [["templates", "Checklist Templates", LayoutTemplate]]
              : []),
          ].map(([value, label, Icon]) => (
            <button
              key={value}
              type="button"
              onClick={() => setActiveTab(value)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-t-xl px-4 py-3 text-sm font-semibold ${activeTab === value ? "bg-white text-gray-950" : "text-gray-300 hover:bg-white/10"}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 rounded-2xl border border-gray-200 bg-white p-3">
        {STATUSES.map((status) => (
          <StatusBadge key={status.id} status={status.id} compact />
        ))}
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
          {apiMessage(error, "Failed to load equipment.")}
        </div>
      )}
      {loading && !items.length ? (
        <div className="flex justify-center p-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      ) : (
        <>
          {activeTab === "overview" && (
            <EquipmentOverview
              data={overview}
              dimension={dimension}
              onDimensionChange={changeDimension}
              onOpenGroup={openGroup}
            />
          )}
          {activeTab === "register" && (
            <EquipmentRegister
              items={items}
              options={options}
              onOpen={(id) => {
                setSelectedEquipmentId(id);
                setSelectedTaskId(null);
              }}
              filters={filters}
              setFilters={setFilters}
            />
          )}
          {activeTab === "tasks" && (
            <TasksView
              tasks={tasks}
              onOpenTask={(task) => {
                setSelectedEquipmentId(task.equipment_id);
                setSelectedTaskId(task.id);
              }}
            />
          )}
          {activeTab === "templates" && (
            <ChecklistTemplatesView
              projectId={projectId}
              options={options}
              canManage={canDesignTemplates}
            />
          )}
        </>
      )}

      {createOpen && (
        <CreateEquipmentModal
          options={options}
          projectId={projectId}
          onClose={() => setCreateOpen(false)}
          onCreated={(created) => {
            setCreateOpen(false);
            reload({ dimension });
            setSelectedEquipmentId(created.id);
          }}
        />
      )}
      {selectedEquipmentId && (
        <EquipmentDrawer
          key={`${projectId}-${selectedEquipmentId}`}
          projectId={projectId}
          equipmentId={selectedEquipmentId}
          currentUser={currentUser}
          projectRole={projectRole}
          members={projectMembers}
          options={options}
          initialTaskId={selectedTaskId}
          onClose={() => {
            setSelectedEquipmentId(null);
            setSelectedTaskId(null);
          }}
          onChanged={() => reload({ dimension })}
        />
      )}
    </div>
  );
}
